import { create } from 'zustand';
import { api } from '../onboarding/api.ts';
import type { Finding } from '../findings/types.ts';
import { useSession } from '../../state/session.ts';

// Report revisions are saved in the workspace (FP-12): a revision snapshots its findings and
// evidence, carries a content digest, and every export (PDF, Markdown, JSON) renders it server-side.

export interface ReportClaim {
  claimId: string;
  text: string;
  findingId: string;
  severity: Finding['normalizedSeverity'];
  origin: Finding['claimOrigin'];
  reviewState: Finding['analystReviewState'];
  remediationState: Finding['remediationState'];
  retestOutcome: Finding['retestOutcome'];
  evidenceIds: string[];
  /** Snapshot of the finding when the revision was made; older revisions may lack it. */
  description?: string;
  affectedAssets?: string[];
}

export interface ReportEvidenceItem {
  evidenceId: string;
  sourcePointer: string;
  kind: string;
  sha256?: string;
  summary?: string;
  capturedAt?: string;
}

export interface ReportRevision {
  reportId: string;
  revisionNumber: number;
  workspaceId: string;
  audience: 'EXECUTIVE' | 'ANALYST' | 'COMPLIANCE';
  status: 'DRAFT' | 'REVIEWED' | 'RELEASED' | 'SUPERSEDED';
  title: string;
  executiveSummary: string;
  limitations: string[];
  claims: ReportClaim[];
  evidenceItems: ReportEvidenceItem[];
  contentDigest: string;
  createdAt: string;
  releasedAt: string | null;
  /** What the revision's assessment snapshot recorded; older revisions may lack it. */
  assessment?: { objective: string; scopeIncluded: string[]; evidenceCutoff: string };
}

export type ReportFormat = 'pdf' | 'markdown' | 'json';

interface ReportsState {
  workspaceId: string | null;
  reports: ReportRevision[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
  selectedReportId: string | null;
  selectReport: (id: string | null) => void;
  load: (workspaceId: string) => Promise<void>;
  /** Saves a new revision from the given findings; throws the service's error when it refuses. */
  createReport: (
    workspaceId: string,
    input: { audience: ReportRevision['audience']; findingIds: string[]; title?: string },
  ) => Promise<ReportRevision>;
}

const empty = {
  workspaceId: null,
  reports: [],
  loading: false,
  loaded: false,
  error: null,
  selectedReportId: null,
};

const newestFirst = (a: ReportRevision, b: ReportRevision) =>
  b.createdAt.localeCompare(a.createdAt);

export const useReports = create<ReportsState>()((set, get) => ({
  ...empty,

  selectReport: (id) => set({ selectedReportId: id }),

  load: async (workspaceId) => {
    if (get().workspaceId !== workspaceId) set({ ...empty, workspaceId });
    set({ loading: true, error: null });
    try {
      const { reports } = await api.listReportRevisions(workspaceId);
      if (get().workspaceId !== workspaceId) return;
      set({ reports: [...reports].sort(newestFirst), loading: false, loaded: true });
    } catch (error) {
      if (get().workspaceId !== workspaceId) return;
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Reports could not be loaded.',
      });
    }
  },

  createReport: async (workspaceId, input) => {
    const report = await api.createReportRevision(workspaceId, input);
    if (get().workspaceId === workspaceId)
      set((state) => ({ reports: [report, ...state.reports], selectedReportId: report.reportId }));
    return report;
  },
}));

/** Downloads a server-rendered export of a saved revision. */
export async function downloadReport(report: ReportRevision, format: ReportFormat) {
  const { blob, filename } = await api.exportReportRevision(
    report.workspaceId,
    report.reportId,
    report.revisionNumber,
    format,
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// Reports belong to the current workspace session and must not survive an account switch.
useSession.subscribe((state, previous) => {
  if (state.workspace?.id !== previous.workspace?.id || state.actor?.id !== previous.actor?.id) {
    useReports.setState({ ...empty });
  }
});
